import { createSelector } from '@reduxjs/toolkit';
// Types
import Reducers from 'app/types/Reducers';
// Models
import { RootState } from 'app/store';
import ILabel from 'app/models/Label';
import { IBatch } from './DMSBatches.models';
import { ICollection } from 'app/store/DMSCollections/DMSCollections.models';
import { IDocument } from 'app/store/DMSDocuments/DMSDocuments.models';
// Selectors
import {
  selectEntities as selectCollectionEntities
} from 'app/store/DMSCollections/DMSCollections.selectors';
import {
  selectDocumentsIds,
  selectDocumentsEntities
} from 'app/store/DMSDocuments/DMSDocuments.selectors';
// Utilities
import { sortByCreationDate, sortByDate, sortByName } from 'app/utilities/SortBy';

export const ALL_DOCUMENTS_BATCH_ID = 'all-documents';
export const ALL_DOCUMENTS_BATCH_CREATED_ON = '1970-01-01T00:00:00.000Z';

export const selectIds = (state:RootState) => state[Reducers.DMSBatches].ids;
export const selectEntities = (state:RootState) => state[Reducers.DMSBatches].entities;
export const selectFilter = (state:RootState) => state[Reducers.DMSBatches].filter;
export const selectSort = (state:RootState) => state[Reducers.DMSBatches].sort;
export const selectPreferences = (state:RootState) => state[Reducers.DMSBatches].preferences;
export const selectLoading = (state:RootState) => state[Reducers.DMSBatches].loading;

export const selectBatchEntity = createSelector(
  [
    selectEntities,
    (_, props:{ batchId:string }) => props
  ],
  (entities:any, { batchId }) => entities[batchId]
);

export const selectBatches = createSelector(
  [
    selectIds,
    selectEntities,
    selectSort
  ],
  ( ids:string[] | null, entities:Record<string, IBatch>, { direction } ) => {
    if ( !ids ) return null;

    const batches:IBatch[] = [...ids].map((id:string) => entities[id]);

    return batches.sort((a:IBatch, b:IBatch) => sortByCreationDate(a, b, direction))
  }
);

export const selectFilteredBatches = createSelector(
  [
    selectDocumentsIds,
    selectDocumentsEntities,
    selectFilter,
    selectBatches,
    selectSort,
    selectPreferences
  ],
  (
    documentIds:number[] | null,
    documentEntities:Record<number, IDocument>,
    { search },
    batches:IBatch[] | null,
    { direction, sortBy },
    preferences
  ) => {
    if ( !batches || !documentIds ) return null;

    const { hideDeletedDocuments, groupByBatches } = preferences;

    const nonEmptyBatches = batches.filter((batch:IBatch) => batch.documentIds.length);

    const handleSort = (a: number, b: number) => {
      if (groupByBatches) {
        return sortByCreationDate(documentEntities[a], documentEntities[b], direction);
      }
      switch (sortBy) {
        case 'createdOn':
          return sortByCreationDate(documentEntities[a], documentEntities[b], direction);
        case 'modifiedOn':
          return sortByDate(documentEntities[a].modifiedOn, documentEntities[b].modifiedOn, direction);
        case 'name':
          return sortByName(documentEntities[a], documentEntities[b], 'name', direction);
        default:
          return sortByCreationDate(documentEntities[a], documentEntities[b], direction);
      }
    };

    const filterAndSearch = (ids: number[]) => {
      return ids
        .map((id) => documentEntities[id])
        .filter((doc): doc is IDocument => !!doc)
        .filter((doc) => !hideDeletedDocuments || !doc.deleted)
        .filter((doc) => {
          if ( !search ) return true;
          const searchToLowerCase = search.toLowerCase();
          let hasSearch =
            doc.name.toLowerCase().includes(searchToLowerCase) ||
            doc.id.toString().includes(searchToLowerCase);

          if ( !hasSearch && doc.labels?.length ) {
            hasSearch = doc.labels.some((label:ILabel) =>
              label.name.toLowerCase().includes(searchToLowerCase)
            );
          }

          return hasSearch;
        })
        .map((doc) => doc.id);
    };

    if (groupByBatches) {
      return nonEmptyBatches
        .map((batch: IBatch) => {
          const filteredIds = filterAndSearch(batch.documentIds);
          const sortedIds = [...filteredIds].sort((a, b) => handleSort(a, b));
          return { ...batch, documentIds: sortedIds };
        })
        .filter((batch:IBatch) => batch.documentIds.length);
    }

    let allIds = nonEmptyBatches.flatMap((batch) => batch.documentIds);
    allIds = filterAndSearch(allIds);
    const sortedAllIds = [...allIds].sort((a, b) => handleSort(a, b));

    // Fake batch returned when groupByBatches === false.
    // Combines all documents into one batch to keep the render logic unchanged.
    return [
      {
        id: ALL_DOCUMENTS_BATCH_ID,
        createdOn: ALL_DOCUMENTS_BATCH_CREATED_ON,
        comment: undefined,
        coverLetterFile: undefined,
        additional: false,
        documentIds: sortedAllIds,
        attachments: [],
        attachmentsIds: [],
        documents: [],
        documentsIds: [],
      } satisfies IBatch
    ];
  }
);

export const selectFilteredBatchesByCollection = createSelector(
  [
    selectDocumentsEntities,
    selectFilter,
    selectFilteredBatches
  ],
  (documentEntities:Record<number, IDocument>, { collectionId }, batches:IBatch[] | null ) => {
    if ( !batches ) return null;

    const noCollectionId = typeof collectionId === 'undefined' || collectionId === 0;

    if ( noCollectionId ) return batches;

    return batches
      .map((batch:IBatch) => {
        const documentIds = batch.documentIds.filter((id:number) => {
          const documentEntity = documentEntities[id];

          const hasCollection = noCollectionId ? true : documentEntity.collectionId === collectionId;

          return hasCollection;
        });
        return { ...batch, documentIds };
      })
      .filter((batch:IBatch) => batch.documentIds.length)
  }
);

export const selectBatchTotalPages = createSelector(
  [
    selectDocumentsEntities,
    (_, props:{ documentIds:number[] }) => props
  ],
  (documentEntities:Record<number, IDocument>, { documentIds }) => {
    return documentIds.reduce((acc:number, cur:number) => {
      const documentEntity = documentEntities[cur];
      return documentEntity.numberOfPages ? acc + documentEntity.numberOfPages : acc;
    }, 0);
  }
);

export const selectCollectionTotalDocuments = createSelector(
  [
    selectDocumentsEntities,
    selectFilteredBatches
  ],
  ( documentEntities:Record<number, IDocument>, batches:IBatch[] | null ) => {
    if ( !batches ) return null;

    const result:Record<number, number> = {}
    for ( let batch of batches ){
      if ( !batch.documentIds.length ) continue;
      for ( let documentId of batch.documentIds ){
        const documentEntity = documentEntities[documentId];
        if ( result[documentEntity.collectionId] ){
          result[documentEntity.collectionId] = result[documentEntity.collectionId] + 1;
        } else {
          result[documentEntity.collectionId] = 1;
        }
      }
    }
    return result;
  }
);

export const selectCollectionTotalPages = createSelector(
  [
    selectCollectionEntities,
    selectDocumentsEntities,
    selectFilter,
    selectFilteredBatchesByCollection
  ],
  (collectionEntities:Record<number,ICollection>, documentEntities:Record<number, IDocument>, { collectionId }, batches:IBatch[] | null) => {
    if ( !batches ) return 0;

    if ( collectionId ){
      const collectionEntity = collectionEntities[collectionId];
      if ( collectionEntity && !collectionEntity.applyOcr ) return null;
    }

    return batches.reduce((acc:number, cur:IBatch) => {
      if ( cur.documentIds && cur.documentIds.length ){
        const total = cur.documentIds.reduce((a:number, c:number) => {
          const documentEntity = documentEntities[c];
          if ( !documentEntity.deleted && documentEntity.ocred && documentEntity.numberOfPages ){
            a = a + documentEntity.numberOfPages;
          }
          return a;
        }, 0);
        acc = acc + total
      }
      return acc;
    }, 0);
  }
);

// ToDO: Will be removed once page store will be reworked
export const selectBatchesWithDocuments = createSelector(
  [
    selectIds,
    selectEntities
  ],
  (ids:string[] | null, entities:Record<string, IBatch>) => {
    if ( !ids ) return [];
    return ids
      .map((id:string) => entities[id])
      .filter((batch:IBatch) => batch.documents && batch.documents.length)
    ;
  }
);

export const selectSortedDocumentIds = createSelector(
  [ selectBatchesWithDocuments ],
  (batches:IBatch[] | null) => {
    if ( !batches ) return null;
    return batches
      .sort((a, b) => sortByCreationDate(a, b, 'asc'))
      .reduce((acc:any, cur:any) => {
        return [...acc, ...cur.documentIds]
      }, []);
  }
)
